/**
 * @license
 *-------------------------------------------------------------------------------------------
 * Copyright © 2025 Progress Software Corporation. All rights reserved.
 * Licensed under commercial license. See LICENSE.md in the package root for more information
 *-------------------------------------------------------------------------------------------
 */
import * as n from "react";
import s from "prop-types";
import { Animation as J } from "@progress/kendo-react-animation";
import { FOCUSABLE_ELEMENTS as Q, Navigation as V, classNames as W } from "@progress/kendo-react-common";
import { ActionSheetItem as P } from "./ActionSheetItem.mjs";
import { headerDisplayName as A } from "./ActionSheetHeader.mjs";
import { footerDisplayName as X } from "./ActionSheetFooter.mjs";
import { contentDisplayName as Y } from "./ActionSheetContent.mjs";
const K = n.forwardRef((e, _) => {
  var O, T;
  const {
    navigatableElements: H = b.navigatableElements,
    navigatable: y = b.navigatable,
    position: L = b.position,
    actions: N,
    filter: g
  } = e, $ = (t, i, a) => {
    a.preventDefault(), a.shiftKey ? i.focusPrevious(t) : i.focusNext(t);
  }, B = (t, i, a) => {
    if (t.ariaDisabled)
      return;
    const h = t.className && t.className.indexOf ? t.className.indexOf("k-actionsheet-item") !== -1 : !1, E = i.elements.filter((o) => o.className.indexOf("k-actionsheet-item") !== -1);
    if (h && e.onItemClick) {
      a.preventDefault();
      const o = e.items[E.indexOf(t)];
      e.onItemClick.call(void 0, {
        syntheticEvent: a,
        item: o,
        title: o && o.title
      });
    }
    if (h && e.onItemSelect) {
      a.preventDefault();
      const o = e.items[E.indexOf(t)];
      e.onItemSelect.call(void 0, {
        syntheticEvent: a,
        item: o,
        title: o && o.title
      });
    }
    e.animation || r();
  }, F = (t, i, a) => {
    e.onOverlayClick && (a.preventDefault(), e.onOverlayClick.call(void 0, a)), e.onClose && (a.preventDefault(), e.onClose.call(void 0, a)), e.animation || r();
  }, r = () => {
    m({ show: !1 });
  }, M = (t) => {
    y && f.current.triggerKeyboardEvent(t);
  }, U = (t) => {
    e.onOverlayClick && e.onOverlayClick.call(void 0, t), e.onClose && e.onClose.call(void 0, t), e.animation || r();
  }, S = (t) => {
    e.onItemClick && e.onItemClick.call(void 0, t), e.onItemSelect && e.onItemSelect.call(void 0, t), e.animation || r();
  }, j = (t) => {
    const i = {};
    return n.Children.forEach(t, (a) => {
      a && (i[a.type.displayName] = a);
    }), i;
  }, x = n.useRef(null), k = n.useRef(null);
  n.useImperativeHandle(x, () => ({ props: e })), n.useImperativeHandle(
    _,
    () => x.current
  );
  const f = n.useRef(), q = n.useRef(e.animationDuration || 300), v = n.useRef({ bottom: "0", width: "100%" }), C = n.useRef(), [l, z] = n.useState({ show: !1, slide: !1 }), m = (t) => {
    z((i) => ({ ...i, ...t }));
  };
  n.useEffect(() => {
    e.expand && !l.show && m({ show: !0 });
  }, []), n.useEffect(() => {
    const t = k.current;
    if (e.expand && !l.show && m({ show: !0 }), e.expand && l.show && !l.slide && m({ slide: !0 }), !e.expand && l.show && l.slide && m({ slide: !1 }), C !== l && l.slide && t && !e.className && (t.style.setProperty("--kendo-actionsheet-height", "auto"), t.style.setProperty("--kendo-actionsheet-max-height", "none")), t && y) {
      const a = [".k-actionsheet-item", ...[
        ".k-actionsheet-footer",
        ".k-actionsheet-content",
        ".k-actionsheet-titlebar"
      ].map((h) => Q.concat(H).map(
        (E) => `${h} ${E}`
      )).flat()];
      f.current = new V({
        tabIndex: e.tabIndex || 0,
        root: k,
        rovingTabIndex: !1,
        selectors: a,
        keyboardEvents: {
          keydown: {
            Tab: $,
            Enter: B,
            Escape: F
          }
        }
      }), f.current.focusElement(f.current.first, null);
    }
    C.current = { ...l };
  });
  const I = "k-actionsheet-title", D = I, c = (O = e.items) == null ? void 0 : O.filter(
    (t) => !t.group || t.group === "top"
  ), d = (T = e.items) == null ? void 0 : T.filter(
    (t) => t.group === "bottom"
  ), G = c && c.length > 0 && d && d.length > 0, u = j(e.children), R = e.animationDuration || q.current, w = l.slide && /* @__PURE__ */ n.createElement(
    "div",
    {
      className: W(`k-actionsheet k-actionsheet-${L}`, e.className),
      role: "dialog",
      "aria-modal": "true",
      "aria-hidden": !1,
      "aria-labelledby": D,
      ref: k,
      onKeyDown: M
    },
    u[A] && !e.title && !e.subTitle && u[A],
    (e.title || e.subTitle) && /* @__PURE__ */ n.createElement("div", { className: "k-actionsheet-titlebar" }, /* @__PURE__ */ n.createElement("div", { className: "k-actionsheet-titlebar-group k-hbox" }, /* @__PURE__ */ n.createElement("div", { className: I, id: D }, /* @__PURE__ */ n.createElement("div", { className: "k-text-center" }, e.title), /* @__PURE__ */ n.createElement("div", { className: "k-actionsheet-subtitle k-text-center" }, e.subTitle)), N && /* @__PURE__ */ n.createElement("div", { className: "k-actionsheet-actions" }, N)), g && /* @__PURE__ */ n.createElement("div", { className: "k-actionsheet-titlebar-group k-actionsheet-filter" }, g)),
    u[Y] || /* @__PURE__ */ n.createElement("div", { className: "k-actionsheet-content" }, /* @__PURE__ */ n.createElement("div", { className: "k-list-ul", role: "group" }, c && c.map((t, i) => /* @__PURE__ */ n.createElement(
      P,
      {
        ...t,
        id: i,
        key: i,
        item: t,
        tabIndex: e.tabIndex || 0,
        onClick: S
      }
    )), G && /* @__PURE__ */ n.createElement("hr", { className: "k-hr" }), d && d.map((t, i) => /* @__PURE__ */ n.createElement(
      P,
      {
        ...t,
        id: i + ((c == null ? void 0 : c.length) || 0),
        key: i,
        item: t,
        tabIndex: e.tabIndex || 0,
        onClick: S
      }
    )))),
    u[X]
  );
  return /* @__PURE__ */ n.createElement(n.Fragment, null, e.expand || l.show ? /* @__PURE__ */ n.createElement("div", { className: "k-actionsheet-container" }, /* @__PURE__ */ n.createElement("div", { className: "k-overlay", onClick: U }), e.animation ? /* @__PURE__ */ n.createElement(
    J,
    {
      transitionName: l.slide ? "slide-up" : "slide-down",
      onExited: r,
      transitionEnterDuration: R,
      transitionExitDuration: R,
      animationEnteringStyle: e.animationStyles || v.current,
      animationEnteredStyle: e.animationStyles || v.current,
      animationExitingStyle: e.animationStyles || v.current,
      exit: !0,
      enter: !0,
      appear: !1
    },
    w
  ) : w) : null);
}), b = {
  navigatable: !0,
  navigatableElements: [],
  position: "bottom"
};
K.propTypes = {
  items: s.array,
  subTitle: s.string,
  title: s.string,
  navigatable: s.bool,
  navigatableElements: s.array,
  position: s.oneOf(["top", "bottom", "left", "right", "fullscreen"])
};
K.displayName = "KendoReactActionSheet";
export {
  K as ActionSheet,
  b as actionSheetDefaultProps
};
